import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { UserDetails } from "@knowt/syncing/graphql/schema";
import ProfilePageInviteLink from "./ProfilePageInviteLink";
import FollowStatsCard from "./FollowStatsCard";
import LinearProgress from "@/components/LinearProgress";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { useRouter } from "@bprogress/next";
import Image from "next/image";
import { ASSETS_URL } from "@/config/deployConstants";
import { getRewardTierStats } from "@knowt/syncing/hooks/gamification/constants";
import { useThemeContextSelector } from "@/utils/theme";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";

type ProfileSideCardProps = {
    isOwner: boolean;

    userInfo: UserDetails;
    className?: string;
    style?: React.CSSProperties;
};

const ProfileSideCard = ({ isOwner, userInfo, className, style }: ProfileSideCardProps) => {
    const { user } = useCurrentUser();

    if (isOwner) {
        const { currentTierReward } = getRewardTierStats(user?.refList_v2?.length || 0);

        return (
            <FlexColumn
                id="refer"
                className={className}
                style={{
                    background: isOwner ? themeColors.lightBlueGradient : themeColors.neutralWhite,
                    padding: `0 ${spacing.MD}`,
                    borderRadius: `${spacing.MD_S}`,
                    ...style,
                }}>
                <p
                    className="bodyBold2"
                    style={{
                        padding: `${spacing.MD_S} ${spacing.SM}`,
                        color: themeColors.neutralBlack,
                        width: "30rem",
                    }}>
                    Get {currentTierReward} coins for each friend you refer
                </p>

                <FlexColumn style={{ flex: 1, flexShrink: 0, width: "100%" }}>
                    <ReferralCoinsCard user={user} />
                    <RedeemCoinsCard />
                    <FlexColumn style={{ marginBottom: `${spacing.MD_S}` }}>
                        <ProfilePageInviteLink user={user} />
                    </FlexColumn>
                </FlexColumn>
            </FlexColumn>
        );
    }

    return (
        <FlexColumn
            id="refer"
            className={className}
            style={{
                background: isOwner ? themeColors.lightBlueGradient : themeColors.neutralWhite,
                padding: `0 ${spacing.MD}`,
                borderRadius: `${spacing.MD_S}`,
                ...style,
            }}>
            <FlexColumn style={{ flex: 1, flexShrink: 0, width: "100%" }}>
                <FlexColumn style={{ marginBottom: `${spacing.MD_S}` }}>
                    <FollowStatsCard userId={userInfo?.ID as string} style={{ minHeight: "418.26px" }} />
                </FlexColumn>
            </FlexColumn>
        </FlexColumn>
    );
};

const ReferralCoinsCard = ({ user }: { user?: UserDetails }) => {
    const { remainingReferrals, collectedCoins, totalCoins, nextTierReward } = getRewardTierStats(
        user?.refList_v2?.length || 0
    );

    return (
        <FlexColumn
            id="profile-screen-referral-rewards"
            style={{
                marginBottom: `${spacing.MD_S}`,
                backgroundColor: themeColors.neutralWhite,
                padding: `${spacing.MD_S}`,
                borderRadius: `${spacing.MD_S}`,
                gap: `${spacing.MD}`,
            }}>
            <FlexRowAlignCenter style={{ gap: spacing.SM, width: "100%" }}>
                <Image src={`${ASSETS_URL}/images/knowt-coin.svg`} alt="Knowt Coin" width={50} height={50} />
                <FlexColumn style={{ flex: 1 }}>
                    <span className="secondaryTextBold1">Referral coins earned</span>
                    <span className="bodyBold1">
                        {collectedCoins}
                        <span style={{ color: themeColors.neutral2 }}>/{totalCoins + nextTierReward}</span>
                    </span>
                </FlexColumn>
            </FlexRowAlignCenter>
            <LinearProgress
                variant="determinate"
                value={(collectedCoins * 100) / (totalCoins + nextTierReward)}
                barColor={themeColors.neutralBlack}
                backgroundColor={themeColors.neutral1}
                height="10px"
                borderRadius={40}
            />
            <span className="secondaryText2" style={{ color: themeColors.neutral3, width: "25rem" }}>
                After {remainingReferrals} referrals it'll increase to {nextTierReward} coins for each referral
            </span>
        </FlexColumn>
    );
};

const RedeemCoinsCard = () => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const router = useRouter();

    return (
        <FlexColumn
            style={{
                marginBottom: `${spacing.MD}`,
                backgroundColor: themeColors.neutralWhite,
                padding: `${spacing.MD_S}`,
                borderRadius: `${spacing.MD_S}`,
                gap: `${spacing.MD}`,
            }}>
            <FlexRowAlignCenter style={{ gap: spacing.SM }}>
                <div
                    style={{
                        width: 50,
                        height: 50,
                        borderRadius: "50%",
                        backgroundColor: isDarkMode ? themeColors.flashcardsMed : themeColors.flashcardsLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                    <Image src={`${ASSETS_URL}/images/shop-icon-v2.svg`} alt="Shop Icon" width={30} height={30} />
                </div>
                <p className="secondaryText2">
                    <span className="secondaryTextBold2">Redeem coins</span> for the free <br /> Knowt
                    <span className="secondaryTextBold2"> merch</span> & feature <br />
                    <span>unlocks </span>
                </p>
            </FlexRowAlignCenter>
            <CircularRectTextButton
                id="profile-screen-store-button"
                onClick={() => router.push("/store")}
                className="secondaryTextBold1"
                style={{
                    border: `1px solid ${themeColors.neutral1}`,
                    padding: `${spacing.XS_2} ${spacing.SM}`,
                    fontSize: "1.5rem",
                }}>
                {"View Store"}
            </CircularRectTextButton>
        </FlexColumn>
    );
};

export default ProfileSideCard;
